import React, { useEffect, useMemo, useState, ReactNode } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import {
  SignedIn,
  SignedOut,
  SignIn,
  SignUp,
  RedirectToSignIn,
  useAuth,
} from "@clerk/clerk-react";
import { setApiTokenGetter } from "./services/api";
import { DocsSliderProvider } from "./help-docs-panel/DocsSliderProvider";
import { StubChatbotTransport } from "./help-docs-panel/StubChatbotTransport";
import type { DocsManifest } from "./help-docs-panel/types";
import { useHashedSessionKey } from "./auth/useHashedSessionKey";
import { ErrorReporter, ErrorHandler } from "./utils/errorHandler";
import {
  UIErrorDisplayProvider,
  useUIErrorDisplay,
} from "./components/providers";
import Dashboard from "./pages/Dashboard";
import {
  ComplianceModule,
  ComplianceDashboard,
  ComplianceRegister,
  InspectionRecords,
  ContractorRegister,
  EvidenceFiles,
  ComplianceReports,
  Notifications,
  AuditTrail,
} from "./modules/compliance";
import Layout from "./components/Layout";
import BuildingsList from "./pages/buildings/BuildingsList";
import BuildingDetails from "./pages/buildings/BuildingDetails";
import FinancialRecordsList from "./pages/financial-records/FinancialRecordsList";
import FinancialRecordDetails from "./pages/financial-records/FinancialRecordDetails";
import JournalEntries from "./pages/journal/JournalEntries";
import JournalNewEntry from "./pages/journal/JournalNewEntry";
import JournalEntryDetail from "./pages/journal/JournalEntryDetail";
import BudgetsList from "./pages/budgets/BudgetsList";
import BudgetDetails from "./pages/budgets/BudgetDetails";
import BudgetCreateEdit from "./pages/budgets/BudgetCreateEdit";
import StatementsList from "./pages/statements/StatementsList";
import StatementCreate from "./pages/statements/StatementCreate";
import StatementDetails from "./pages/statements/StatementDetails";
import UsersList from "./pages/users/UsersList";
import UserDetails from "./pages/users/UserDetails";
import UserInvite from "./pages/users/UserInvite";
import UnitsList from "./pages/units/UnitsList";
import UnitDetails from "./pages/units/UnitDetails";
import ContactsList from "./pages/contacts/ContactsList";
import ContactDetails from "./pages/contacts/ContactDetails";
import ContactEdit from "./pages/contacts/ContactEdit";
import ContactAdd from "./pages/contacts/ContactAdd";
import MeetingsList from "./pages/meetings/MeetingsList";
import MeetingDetails from "./pages/meetings/MeetingDetails";
import ReportsHub from "./pages/reports/ReportsHub";
import SettingsPage from "./pages/settings/SettingsPage";
import NotFoundPage from "./pages/NotFoundPage";
import LandingPage from "./pages/LandingPage";
import BuildingSelectionPage from "./pages/BuildingSelectionPage";
import BankingList from "./pages/banking/BankingList";
import BankAccountNew from "./pages/banking/BankAccountNew";
import BankAccountDetails from "./pages/banking/BankAccountDetails";
import BankTransactionNew from "./pages/banking/BankTransactionNew";
import BankTransactionEdit from "./pages/banking/BankTransactionEdit";
import ExplainTransaction from "./pages/banking/ExplainTransaction";
import CalendarPage from "./pages/calendar/CalendarPage";
import CategoriesList from "./pages/categories/CategoriesList";
import FreeAgentImport from "./pages/import/FreeAgentImport";
import ManagementFeesList from "./pages/management-fees/ManagementFeesList";

/**
 * Wraps the authenticated dashboard content in a `DocsSliderProvider` so the
 * help-docs slider and stub chatbot dock are available across every route
 * under `/dashboard/*`. Rendered inside `<SignedIn>` so `sessionKey` (derived
 * from the authenticated Clerk user) is defined.
 *
 * The docs manifest is fetched once on mount from
 * `${PUBLIC_URL}/docs/docs.manifest.json`. Until the manifest resolves the
 * children render without the provider so the app stays functional; the
 * slider simply becomes available once the fetch completes.
 */
function DocsSliderShellWrapper({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const location = useLocation();
  const sessionKey = useHashedSessionKey();
  const [manifest, setManifest] = useState<DocsManifest | null>(null);

  // Stable transport identity — matters because `useChatbotClient()` (and
  // other consumers) memoise on transport identity. Instantiated once per
  // shell mount.
  const transport = useMemo(() => new StubChatbotTransport(), []);

  useEffect(() => {
    let cancelled = false;
    const base = process.env.PUBLIC_URL ?? "";
    const url = `${base}/docs/docs.manifest.json`;

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Failed to load docs manifest: HTTP ${response.status}`,
          );
        }
        return response.json() as Promise<DocsManifest>;
      })
      .then((json) => {
        if (!cancelled) {
          setManifest(json);
        }
      })
      .catch((err: unknown) => {
        // Docs slider is a progressive enhancement — a failed manifest
        // fetch must never break the app. Log for diagnostics only.
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("[help-docs-panel] Failed to load docs manifest:", err);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (manifest === null) {
    return <>{children}</>;
  }

  return (
    <DocsSliderProvider
      sessionKey={sessionKey}
      transport={transport}
      currentRoute={location.pathname}
      manifest={manifest}
    >
      {children}
    </DocsSliderProvider>
  );
}

function AppContent() {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const uiDisplay = useUIErrorDisplay();

  // Initialize ErrorHandler with UIErrorDisplay on app startup
  useEffect(() => {
    ErrorHandler.setUIDisplay(uiDisplay);
  }, [uiDisplay]);

  // Initialize error reporter on app startup
  useEffect(() => {
    ErrorReporter.initialize();
  }, []);

  // Wire Clerk's getToken into the axios interceptor so all API calls are authenticated
  useEffect(() => {
    setApiTokenGetter(getToken);
  }, [getToken]);

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />

      <Route
        path="/dashboard/*"
        element={
          <>
            <SignedIn>
              <DocsSliderShellWrapper>
                <Layout>
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="buildings" element={<BuildingsList />} />
                    <Route path="buildings/:id" element={<BuildingDetails />} />
                    <Route
                      path="financial-records"
                      element={<FinancialRecordsList />}
                    />
                    <Route
                      path="financial-records/new"
                      element={<FinancialRecordDetails />}
                    />
                    <Route
                      path="financial-records/:id"
                      element={<FinancialRecordDetails />}
                    />
                    <Route path="categories" element={<CategoriesList />} />
                    <Route path="journal" element={<JournalEntries />} />
                    <Route
                      path="journal/new-entry"
                      element={<JournalNewEntry />}
                    />
                    <Route
                      path="journal/:id"
                      element={<JournalEntryDetail />}
                    />
                    <Route path="budgets" element={<BudgetsList />} />
                    <Route path="budgets/new" element={<BudgetCreateEdit />} />
                    <Route path="budgets/:id" element={<BudgetDetails />} />
                    <Route
                      path="budgets/:id/edit"
                      element={<BudgetCreateEdit />}
                    />
                    <Route path="statements" element={<StatementsList />} />
                    <Route
                      path="statements/new"
                      element={<StatementCreate />}
                    />
                    <Route
                      path="statements/:id"
                      element={<StatementDetails />}
                    />
                    <Route path="users" element={<UsersList />} />
                    <Route path="users/invite" element={<UserInvite />} />
                    <Route path="users/:id" element={<UserDetails />} />
                    <Route path="units" element={<UnitsList />} />
                    <Route path="units/:id" element={<UnitDetails />} />
                    <Route path="contacts" element={<ContactsList />} />
                    <Route path="contacts/new" element={<ContactAdd />} />
                    <Route path="contacts/:id" element={<ContactDetails />} />
                    <Route path="contacts/:id/edit" element={<ContactEdit />} />
                    <Route path="calendar" element={<CalendarPage />} />
                    <Route path="meetings" element={<MeetingsList />} />
                    <Route path="meetings/:id" element={<MeetingDetails />} />
                    <Route path="reports/*" element={<ReportsHub />} />
                    <Route path="banking" element={<BankingList />} />
                    <Route path="banking/new" element={<BankAccountNew />} />
                    <Route
                      path="banking/:id"
                      element={<BankAccountDetails />}
                    />
                    <Route
                      path="banking/:id/transactions/new"
                      element={<BankTransactionNew />}
                    />
                    <Route
                      path="banking/:id/transactions/:txId/explain"
                      element={<ExplainTransaction />}
                    />
                    <Route
                      path="banking/:id/transactions/:txId/edit"
                      element={<BankTransactionEdit />}
                    />
                    <Route path="settings/*" element={<SettingsPage />} />
                    <Route
                      path="import/freeagent"
                      element={<FreeAgentImport />}
                    />
                    <Route
                      path="management-fees"
                      element={<ManagementFeesList />}
                    />
                    <Route path="compliance" element={<ComplianceModule />}>
                      <Route index element={<ComplianceDashboard />} />
                      <Route path="register" element={<ComplianceRegister />} />
                      <Route
                        path="inspections"
                        element={<InspectionRecords />}
                      />
                      <Route
                        path="contractors"
                        element={<ContractorRegister />}
                      />
                      <Route path="evidence" element={<EvidenceFiles />} />
                      <Route path="reports" element={<ComplianceReports />} />
                      <Route path="notifications" element={<Notifications />} />
                      <Route path="audit-trail" element={<AuditTrail />} />
                    </Route>
                    {/* Catch-all route for any unmatched paths under dashboard */}
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </Layout>
              </DocsSliderShellWrapper>
            </SignedIn>
            <SignedOut>
              <RedirectToSignIn />
            </SignedOut>
          </>
        }
      />

      {/* Auth related routes - redirect back to landing page with modals */}
      <Route
        path="/sign-in/*"
        element={
          isSignedIn ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <SignIn
              routing="path"
              path="/sign-in"
              afterSignInUrl="/dashboard"
              redirectUrl="/dashboard"
            />
          )
        }
      />
      <Route
        path="/sign-up/*"
        element={
          isSignedIn ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <SignUp
              routing="path"
              path="/sign-up"
              afterSignUpUrl="/dashboard"
              redirectUrl="/dashboard"
            />
          )
        }
      />

      {/* Building selection after sign in */}
      <Route
        path="/select-building"
        element={
          <SignedIn>
            <BuildingSelectionPage />
          </SignedIn>
        }
      />

      {/* Catch-all route for any unmatched paths */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <UIErrorDisplayProvider>
      <AppContent />
    </UIErrorDisplayProvider>
  );
}

export default App;
